import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Shield, MapPin, Car, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import MapView from "@/components/chauffeur/MapView";

const STATUS_LABEL = {
  requested: "Searching for driver",
  accepted: "Driver assigned",
  arriving: "Driver is arriving",
  in_progress: "On the way",
  completed: "Trip complete",
  cancelled: "Cancelled",
};

export default function TripSharePage() {
  const { shareToken } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const res = await api.get(`/chauffeur/share/${shareToken}`);
      setTrip(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid link");
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [shareToken]); // eslint-disable-line

  if (error) {
    return <div data-testid="trip-share-error" className="pt-32 min-h-screen bg-luxury-black flex items-center justify-center text-white/60 font-body">{error}</div>;
  }
  if (!trip) {
    return <div className="pt-32 min-h-screen bg-luxury-black flex items-center justify-center text-gold/60 font-body">Loading shared trip…</div>;
  }

  return (
    <div data-testid="trip-share-page" className="pt-24 min-h-screen bg-luxury-black">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8">
        <div className="mb-8">
          <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-1 flex items-center gap-1"><Shield size={12} /> Live Trip Share</p>
          <h1 className="font-heading text-3xl text-white">{trip.client_first_name}'s Trip</h1>
          <p className="text-white/50 font-body text-sm mt-1">{STATUS_LABEL[trip.status] || trip.status}</p>
        </div>
        {trip.sos_triggered && (
          <div data-testid="trip-sos-banner" className="mb-4 p-4 border border-red-500/40 bg-red-500/10 rounded-md flex items-center gap-3">
            <AlertTriangle className="text-red-400" />
            <span className="text-red-300 font-body text-sm">SOS triggered on this trip — admin notified.</span>
          </div>
        )}
        <MapView pickup={trip.pickup} dropoff={trip.dropoff} height={420} data-testid="trip-share-map" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <div className="p-4 border border-white/10 bg-white/5 rounded-md">
            <p className="text-white/50 text-xs font-body uppercase tracking-wider mb-1"><MapPin size={10} className="inline" /> Pickup</p>
            <p className="text-white text-sm font-body">{trip.pickup?.address}</p>
          </div>
          <div className="p-4 border border-white/10 bg-white/5 rounded-md">
            <p className="text-white/50 text-xs font-body uppercase tracking-wider mb-1"><MapPin size={10} className="inline" /> Drop-off</p>
            <p className="text-white text-sm font-body">{trip.dropoff?.address}</p>
          </div>
          {trip.driver_name && (
            <div className="p-4 border border-gold/20 bg-gold/5 rounded-md sm:col-span-2">
              <p className="text-gold text-xs font-body uppercase tracking-wider mb-1"><Car size={10} className="inline" /> Driver</p>
              <p className="text-white text-sm font-body">{trip.driver_name}</p>
              <p className="text-white/60 text-xs font-body">{trip.driver_vehicle} · {trip.driver_plate}</p>
            </div>
          )}
        </div>
        <p className="text-white/30 text-xs font-body text-center mt-8">This page auto-refreshes every 5s.</p>
      </div>
    </div>
  );
}
